import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import MDAvatar from "@/components/atoms/MDAvatar";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import { useNotification } from "@/contexts/notification-context";
import useToast from "@/contexts/toast-context";
import { buildFullPhone, formatPhoneNumber } from "@/helpers/phone-helpers";
import { getAvatarLetters } from "@/helpers/profile-helpers";
import IContact from "@/interfaces/contact.interface";
import { IOrder } from "@/interfaces/order.interfaces";
import { ChatMessages } from "@/messages/chat/chat.messages";
import { CURRENT_CHAT_INPUT_MESSAGES } from "@/messages/chat/current-chat-input.messages";
import { TransferStepLabel } from "@/components/screens/Chat/components/TransferStepLabel";
import { useChatAttendanceActions } from "@/hooks/chat/use-chat-attendance-actions";
import contactService from "@/services/contact.service";
import orderService from "@/services/order.service";
import { FilterType } from "@/types/filter.types";

type AttendanceActions = ReturnType<typeof useChatAttendanceActions>;

type Props = {
  messages: ChatMessages;
  isExpanded: boolean;
  onToggle: () => void;
  attendanceActions: AttendanceActions;
};

function parseContactTags(tags: IContact["tags"] | undefined): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    return tags
      .map((tag) =>
        String(tag)
          .replace(/^\[|\]$/g, "")
          .trim()
      )
      .filter(Boolean);
  }

  try {
    const parsed = JSON.parse(tags);
    if (Array.isArray(parsed)) {
      return parsed
        .map((tag) =>
          String(tag)
            .replace(/^\[|\]$/g, "")
            .trim()
        )
        .filter(Boolean);
    }
  } catch {
    // fallback: comma-separated
  }

  return String(tags)
    .split(",")
    .map((tag) => tag.replace(/^\[|\]$/g, "").trim())
    .filter(Boolean);
}

type InfoRowProps = {
  icon: ReactNode;
  value: string;
  onClick?: () => void;
  canCopy?: boolean;
  onCopy?: () => void;
  copyLabel: string;
};

function InfoRow({ icon, value, onClick, canCopy, onCopy, copyLabel }: InfoRowProps) {
  return (
    <MDBox
      display="flex"
      alignItems="center"
      gap={1.5}
      py={0.75}
      sx={{
        "&:hover .copy-action": { opacity: 1 },
      }}
    >
      <MDBox display="flex" alignItems="center" color="text" sx={{ flexShrink: 0 }}>
        {icon}
      </MDBox>

      {onClick ? (
        <Link
          component="button"

          underline="hover"
          color="inherit"
          onClick={onClick}
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <MDTypography component="span" variant="button" fontWeight="bold" color="dark">
            {value}
          </MDTypography>
        </Link>
      ) : (
        <MDTypography
          variant="button"
          fontWeight="regular"
          color="text"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </MDTypography>
      )}

      {canCopy && (
        <Tooltip title={copyLabel}>
          <IconButton
            className="copy-action"
            size="small"
            onClick={onCopy}
            sx={{
              opacity: 0.7,
              transition: "opacity 0.15s ease",
              flexShrink: 0,
            }}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </MDBox>
  );
}

type CollapsibleSectionProps = {
  title: string;
  icon: ReactNode;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  disabled?: boolean;
};

function CollapsibleSection({
  title,
  icon,
  open,
  onToggle,
  children,
  disabled = false,
}: CollapsibleSectionProps) {
  return (
    <MDBox
      borderRadius="lg"
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        overflow: "hidden",
        opacity: disabled ? 0.45 : 1,
      })}
    >
      <MDBox
        component="button"
        {...{ type: "button", disabled: disabled }}
        onClick={onToggle}
        display="flex"
        alignItems="center"
        width="100%"
        px={1.5}
        py={1.25}
        gap={1}
        sx={{
          border: 0,
          background: "transparent",
          cursor: disabled ? "default" : "pointer",
          textAlign: "left",
        }}
      >
        <MDBox display="flex" color="text">
          {icon}
        </MDBox>
        <MDTypography variant="button" fontWeight="medium" color="dark">
          {title}
        </MDTypography>
        <MDBox display="flex" color="text" ml="auto">
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </MDBox>
      </MDBox>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider sx={{ my: 0 }} />
        <MDBox px={1.5} py={1.25}>
          {children}
        </MDBox>
      </Collapse>
    </MDBox>
  );
}

function ContactDataPanel({ messages, isExpanded, onToggle, attendanceActions }: Props) {
  const { selectedChat } = useNotification();
  const { lang } = useLanguage();
  const { showToast } = useToast();
  const [contact, setContact] = useState<IContact | null>(null);
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [hasLoadedOrders, setHasLoadedOrders] = useState(false);
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const [isOrdersOpen, setIsOrdersOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const {
    chatInputEnabled,
    canAssume,
    canFinish,
    assumeControl,
    finishControl,
    transferSteps,
    transferToStep,
  } = attendanceActions;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsOrdersOpen(false);
      setHasLoadedOrders(false);
      setOrders([]);

      if (!selectedChat?.contactId) {
        setContact(null);
        return;
      }

      setIsLoading(true);
      try {
        const contactData = await contactService.getById(selectedChat.contactId);
        if (cancelled) return;
        setContact(contactData);
      } catch {
        if (!cancelled) setContact(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedChat?.contactId]);

  useEffect(() => {
    let cancelled = false;

    const loadOrders = async () => {
      if (!isOrdersOpen || !selectedChat?.contactId || hasLoadedOrders) return;

      setIsLoadingOrders(true);
      try {
        const ordersData = await orderService.filter(
          [{ property: "ContactId" as keyof IOrder, filterType: FilterType.Equals, value: selectedChat.contactId }],
          { pageNumber: 1, pageSize: 5 }
        );

        if (cancelled) return;

        const sortedOrders = [...(ordersData?.items ?? [])].sort((a, b) => {
          const dateA = a.orderDate ? new Date(a.orderDate).getTime() : 0;
          const dateB = b.orderDate ? new Date(b.orderDate).getTime() : 0;
          return dateB - dateA;
        });
        setOrders(sortedOrders.slice(0, 5));
        setHasLoadedOrders(true);
      } catch {
        if (!cancelled) {
          setOrders([]);
          setHasLoadedOrders(true);
        }
      } finally {
        if (!cancelled) setIsLoadingOrders(false);
      }
    };

    loadOrders();
    return () => {
      cancelled = true;
    };
  }, [isOrdersOpen, selectedChat?.contactId, hasLoadedOrders]);

  const openContact = () => {
    if (!selectedChat?.contactId) return;
    window.open(ROUTE_URLS.updateContact(lang, selectedChat.contactId), "_blank");
  };

  const openOrder = (orderId: string | number) => {
    window.open(ROUTE_URLS.orderDetails(lang, String(orderId)), "_blank");
  };

  const fullName = useMemo(() => {
    const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(" ").trim();
    return name || selectedChat?.contactName || messages.contactPanelNoContact;
  }, [contact?.firstName, contact?.lastName, selectedChat?.contactName, messages.contactPanelNoContact]);

  const phone = useMemo(() => {
    if (!contact) return "";
    const fullPhone = buildFullPhone(
      contact.ddiMobile || "",
      contact.dddMobile || "",
      contact.mobilePhone || ""
    );
    return formatPhoneNumber(fullPhone);
  }, [contact]);

  const email = contact?.email?.trim() || "";
  const nickname = contact?.nickName?.trim() || "";
  const location = useMemo(() => {
    const primaryAddress = contact?.addresses?.[0];
    const city = (contact?.addressCity || primaryAddress?.addressCity || "").trim();
    const state = (
      contact?.addressStateUf ||
      contact?.addressState ||
      primaryAddress?.addressStateUf ||
      primaryAddress?.addressState ||
      ""
    ).trim();

    if (city && state) return `${city}, ${state}`;
    return city || state || "";
  }, [contact]);
  const tags = useMemo(() => parseContactTags(contact?.tags), [contact?.tags]);

  const copyValue = useCallback(
    async (value: string) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        showToast({ text: messages.contactPanelCopySuccess, type: "success" });
      } catch {
        showToast({ text: messages.contactPanelCopyError, type: "error" });
      }
    },
    [messages.contactPanelCopyError, messages.contactPanelCopySuccess, showToast]
  );

  if (!isExpanded) {
    return (
      <Card
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 1.5,
          px: 0.5,
          cursor: "pointer",
          overflow: "hidden",
        }}
        onClick={onToggle}
      >
        <IconButton
          size="small"
          color="primary"
          aria-label={messages.contactPanelExpand}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <PersonOutlineIcon color="primary" fontSize="small" sx={{ mt: 1 }} />
      </Card>
    );
  }

  return (
    <Card sx={{ display: "flex", flexDirection: "column", overflow: "hidden", height: "100%", width: "100%" }}>
      <MDBox p={2} display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ flexShrink: 0 }}>
        <MDTypography variant="h6" fontWeight="medium" color="dark">
          {messages.contactPanelTitle}
        </MDTypography>
        <IconButton size="small" color="primary" aria-label={messages.contactPanelCollapse} onClick={onToggle}>
          <ChevronRightIcon />
        </IconButton>
      </MDBox>

      <Divider sx={{ my: 0 }} />

      {!selectedChat ? (
        <MDBox p={2}>
          <MDTypography variant="button" color="text">
            {messages.contactPanelEmptySelection}
          </MDTypography>
        </MDBox>
      ) : isLoading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress color="primary" size={28} />
        </MDBox>
      ) : (
        <MDBox p={2} sx={{ overflowY: "auto", flexGrow: 1, minHeight: 0 }}>
          <MDBox display="flex" alignItems="flex-start" gap={1.5} mb={2}>
            <MDAvatar size="md" bgColor="light" shadow="sm">
              {getAvatarLetters(fullName)}
            </MDAvatar>
            <MDBox flex={1} minWidth={0} lineHeight={1.2}>
              <MDBox display="flex" alignItems="center" gap={0.5}>
                <MDTypography
                  variant="h6"
                  fontWeight="medium"
                  color="dark"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {fullName}
                </MDTypography>
                <Tooltip title={messages.contactPanelOpenContact}>
                  <IconButton size="small" onClick={openContact}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MDBox>
              {nickname && (
                <MDTypography variant="caption" color="text" fontWeight="medium">
                  {nickname}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>

          <MDBox mb={2}>
            <InfoRow
              icon={<PhoneOutlinedIcon fontSize="small" color="inherit" />}
              value={phone || "-"}
              canCopy={!!phone}
              onCopy={() => copyValue(phone)}
              copyLabel={messages.contactPanelCopy}
            />
            <InfoRow
              icon={<EmailOutlinedIcon fontSize="small" color="inherit" />}
              value={email || "-"}
              canCopy={!!email}
              onCopy={() => copyValue(email)}
              copyLabel={messages.contactPanelCopy}
            />
            <InfoRow
              icon={<PlaceOutlinedIcon fontSize="small" color="inherit" />}
              value={location || "-"}
              copyLabel={messages.contactPanelCopy}
            />
          </MDBox>

          <MDBox display="flex" flexDirection="column" gap={1.5}>
            <CollapsibleSection
              title={messages.contactPanelTagsLabel}
              icon={<LocalOfferOutlinedIcon fontSize="small" color="inherit" />}
              open={isTagsOpen}
              onToggle={() => setIsTagsOpen((prev) => !prev)}
            >
              {tags.length === 0 ? (
                <MDTypography variant="caption" color="text">
                  -
                </MDTypography>
              ) : (
                <MDBox display="flex" flexWrap="wrap" gap={0.75}>
                  {tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                  ))}
                </MDBox>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title={messages.contactPanelOrdersTitle}
              icon={<ShoppingBagOutlinedIcon fontSize="small" color="inherit" />}
              open={isOrdersOpen}
              onToggle={() => setIsOrdersOpen((prev) => !prev)}
            >
              {isLoadingOrders ? (
                <MDBox display="flex" justifyContent="center" py={1}>
                  <CircularProgress color="primary" size={22} />
                </MDBox>
              ) : orders.length === 0 ? (
                <MDTypography variant="caption" color="text">
                  {messages.contactPanelNoOrders}
                </MDTypography>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={1}>
                  {orders.map((order) => (
                    <MDBox key={String(order.id)}>
                      <Link
                        component="button"

                        underline="hover"
                        color="inherit"
                        onClick={() => openOrder(order.id)}
                        sx={{ display: "block", textAlign: "left" }}
                      >
                        <MDTypography component="span" variant="button" fontWeight="bold" color="dark">
                          {order.orderNumber}
                        </MDTypography>
                      </Link>
                      {order.orderDate && (
                        <MDTypography variant="caption" color="text" fontWeight="medium">
                          {new Date(order.orderDate).toLocaleDateString("pt-BR")}
                        </MDTypography>
                      )}
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title={messages.contactPanelActionsTitle}
              icon={<TuneOutlinedIcon fontSize="small" color="inherit" />}
              open={isActionsOpen}
              onToggle={() => setIsActionsOpen((prev) => !prev)}
            >
              <MDBox display="flex" flexDirection="column" gap={1}>
                {canFinish && (
                  <MDBox
                    component="button"
                    {...{ type: "button" }}
                    onClick={finishControl}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    px={0.5}
                    py={0.75}
                    sx={{
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "grey.100" },
                    }}
                  >
                    <PersonOutlineIcon fontSize="small" />
                    <MDTypography variant="button" fontWeight="medium" color="dark">
                      {CURRENT_CHAT_INPUT_MESSAGES.finishControl}
                    </MDTypography>
                  </MDBox>
                )}
                {canAssume && (
                  <MDBox
                    component="button"
                    {...{ type: "button" }}
                    onClick={assumeControl}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    px={0.5}
                    py={0.75}
                    sx={{
                      border: 0,
                      background: "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "grey.100" },
                    }}
                  >
                    <PersonOutlineIcon fontSize="small" />
                    <MDTypography variant="button" fontWeight="medium" color="dark">
                      {CURRENT_CHAT_INPUT_MESSAGES.assumeControl}
                    </MDTypography>
                  </MDBox>
                )}
                {transferSteps.length > 0 && (
                  <CollapsibleSection
                    title={messages.contactPanelTransferTitle}
                    icon={<SwapHorizIcon fontSize="small" color="inherit" />}
                    open={isTransferOpen}
                    onToggle={() => setIsTransferOpen((prev) => !prev)}
                  >
                    <MDBox display="flex" flexDirection="column" gap={0.5}>
                      {transferSteps.map((step) => (
                        <MDBox
                          key={`${step.workflowId}-${step.stepName}`}
                          component="button"
                          {...{ type: "button", disabled: !chatInputEnabled }}
                          onClick={() => {
                            if (!chatInputEnabled) return;
                            transferToStep(step);
                          }}
                          display="flex"
                          alignItems="center"
                          gap={0.5}
                          px={0.5}
                          py={0.75}
                          sx={{
                            border: 0,
                            background: "transparent",
                            cursor: chatInputEnabled ? "pointer" : "default",
                            textAlign: "left",
                            borderRadius: 1,
                            opacity: chatInputEnabled ? 1 : 0.45,
                            "&:hover": chatInputEnabled ? { backgroundColor: "grey.100" } : undefined,
                          }}
                        >
                          <SmartToyOutlinedIcon fontSize="small" />
                          <TransferStepLabel step={step} inset={false} />
                        </MDBox>
                      ))}
                    </MDBox>
                  </CollapsibleSection>
                )}
              </MDBox>
            </CollapsibleSection>
          </MDBox>
        </MDBox>
      )}
    </Card>
  );
}

export default ContactDataPanel;
